import * as React from 'react'
import { createPortal } from 'react-dom'
import { XIcon } from 'lucide-react'
import { cn } from '../../lib/utils'
import { useCoachStore } from '../../stores/coachStore'

interface ModalContextValue {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const ModalContext = React.createContext<ModalContextValue | undefined>(undefined)

const useModal = () => {
  const context = React.useContext(ModalContext)
  if (!context) {
    throw new Error('useModal must be used within a Modal')
  }
  return context
}

// Root component that provides context
interface ModalProps {
  open?: boolean
  defaultOpen?: boolean
  onOpenChange?: (open: boolean) => void
  children: React.ReactNode
  modalId?: string // Optional ID to track which modal is open
}

export function Modal({ open: controlledOpen, defaultOpen = false, onOpenChange, children, modalId: _modalId }: ModalProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen)
  
  const open = controlledOpen !== undefined ? controlledOpen : uncontrolledOpen
  const setOpen = React.useCallback((value: boolean) => {
    if (controlledOpen === undefined) {
      setUncontrolledOpen(value)
    }
    onOpenChange?.(value)
  }, [controlledOpen, onOpenChange])

  return (
    <ModalContext.Provider value={{ open, onOpenChange: setOpen }}>
      {children}
    </ModalContext.Provider>
  )
}

// Trigger component
interface ModalTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean
}

export function ModalTrigger({ children, asChild, ...props }: ModalTriggerProps) {
  const { onOpenChange } = useModal()
  
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    props.onClick?.(e)
    if (!e.defaultPrevented) {
      onOpenChange(true)
    }
  }

  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children as React.ReactElement<{ onClick?: (event: React.MouseEvent) => void }>, {
      ...props,
      onClick: handleClick,
    })
  }

  return (
    <button {...props} onClick={handleClick}>
      {children}
    </button>
  )
}

// Portal component
interface ModalPortalProps {
  children: React.ReactNode
  container?: HTMLElement | null
}

export function ModalPortal({ children, container }: ModalPortalProps) {
  const { open } = useModal()
  const [mounted, setMounted] = React.useState(false)

  React.useEffect(() => {
    setMounted(true)
    return () => setMounted(false)
  }, [])

  if (!mounted || !open) return null

  const portalContainer = container || document.querySelector('.app-container') || document.body

  return createPortal(children, portalContainer)
}

// Overlay component
interface ModalOverlayProps extends React.HTMLAttributes<HTMLDivElement> {}

export function ModalOverlay({ className, onClick, ...props }: ModalOverlayProps) {
  const { open, onOpenChange } = useModal()
  const [visible, setVisible] = React.useState(false)

  React.useEffect(() => {
    if (open) {
      // Small delay to trigger animation
      requestAnimationFrame(() => {
        setVisible(true)
      })
    } else {
      setVisible(false)
    }
  }, [open])

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    onClick?.(e)
    if (!e.defaultPrevented && e.target === e.currentTarget) {
      onOpenChange(false)
    }
  }

  return (
    <div
      className={cn(
        'absolute inset-0 z-50 bg-black/50',
        visible ? 'animate-in fade-in-0 duration-200' : 'animate-out fade-out-0 duration-200',
        className
      )}
      onClick={handleClick}
      data-state={open ? 'open' : 'closed'}
      {...props}
    />
  )
}

// Content component
interface ModalContentProps extends React.HTMLAttributes<HTMLDivElement> {
  showCloseButton?: boolean
  onEscapeKeyDown?: (e: KeyboardEvent) => void
  onPointerDownOutside?: (e: MouseEvent) => void
}

export function ModalContent({ 
  className, 
  children, 
  showCloseButton = true,
  onEscapeKeyDown,
  onPointerDownOutside,
  ...props 
}: ModalContentProps) {
  const { open, onOpenChange } = useModal()
  const [visible, setVisible] = React.useState(false)
  const contentRef = React.useRef<HTMLDivElement>(null)
  const { isCoachOpen } = useCoachStore()

  React.useEffect(() => {
    if (open) {
      // Small delay to trigger animation
      requestAnimationFrame(() => {
        setVisible(true)
      })
    } else {
      setVisible(false)
    }
  }, [open])

  // Handle escape key
  React.useEffect(() => {
    if (!open) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // If coach is open, don't close the modal - let the coach handle it
        if (isCoachOpen) {
          return
        }
        
        onEscapeKeyDown?.(e)
        if (!e.defaultPrevented) {
          onOpenChange(false)
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open, onOpenChange, onEscapeKeyDown, isCoachOpen])

  // Handle click outside
  React.useEffect(() => {
    if (!open) return

    const handlePointerDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      
      // Check if click is in the coach panel or on coach toggle button
      const isInCoachPanel = target.closest('.coach-panel')
      const isCoachToggleButton = target.closest('[data-coach-toggle]')
      
      // Don't close modal if clicking in coach area
      if (isInCoachPanel || isCoachToggleButton) {
        return
      }
      
      if (contentRef.current && !contentRef.current.contains(target)) {
        onPointerDownOutside?.(e)
        if (!e.defaultPrevented) {
          onOpenChange(false)
        }
      }
    }

    // Small delay to avoid closing immediately on open
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handlePointerDown)
    }, 100)

    return () => {
      clearTimeout(timeoutId)
      document.removeEventListener('mousedown', handlePointerDown)
    }
  }, [open, onOpenChange, onPointerDownOutside])


  return (
    <ModalPortal>
      <ModalOverlay />
      <div
        ref={contentRef}
        className={cn(
          'absolute left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg',
          'rounded-lg',
          visible ? 'animate-in fade-in-0 zoom-in-95 duration-200' : 'animate-out fade-out-0 zoom-out-95 duration-200',
          className
        )}
        data-state={open ? 'open' : 'closed'}
        {...props}
      >
        {children}
        {showCloseButton && (
          <button
            className="absolute left-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground md:left-auto md:right-4"
            onClick={() => onOpenChange(false)}
          >
            <XIcon className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </button>
        )}
      </div>
    </ModalPortal>
  )
}

// Optional header component
interface ModalHeaderProps extends React.HTMLAttributes<HTMLDivElement> {}

export function ModalHeader({ className, ...props }: ModalHeaderProps) {
  return (
    <div
      className={cn('flex flex-col space-y-1.5 text-center sm:text-left', className)}
      {...props}
    />
  )
}

// Optional footer component
interface ModalFooterProps extends React.HTMLAttributes<HTMLDivElement> {}

export function ModalFooter({ className, ...props }: ModalFooterProps) {
  return (
    <div
      className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className)}
      {...props}
    />
  )
}

// Optional title component
interface ModalTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {}

export function ModalTitle({ className, ...props }: ModalTitleProps) {
  return (
    <h2
      className={cn('text-lg font-semibold leading-none tracking-tight', className)}
      {...props}
    />
  )
}

// Optional description component
interface ModalDescriptionProps extends React.HTMLAttributes<HTMLParagraphElement> {}

export function ModalDescription({ className, ...props }: ModalDescriptionProps) {
  return (
    <p
      className={cn('text-sm text-muted-foreground', className)}
      {...props}
    />
  )
}

// Close button component
interface ModalCloseProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean
}

export function ModalClose({ children, asChild, ...props }: ModalCloseProps) {
  const { onOpenChange } = useModal()
  
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    props.onClick?.(e)
    if (!e.defaultPrevented) {
      onOpenChange(false)
    }
  }

  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children as React.ReactElement<{ onClick?: (event: React.MouseEvent) => void }>, {
      ...props,
      onClick: handleClick,
    })
  }

  return (
    <button {...props} onClick={handleClick}>
      {children}
    </button>
  )
}